import { CreditCardQuestion } from '@hb/domain/steps/financial-obligation';
import { FinancialObligationsStoreService } from '@hb/modules/financial-obligations/financial-obligations-store.service';
import { FieldSubject } from '@hb/domain/field-subject';
import { CreditCardBuilder, money } from '@hb/builder';
import { AbstractStoreInjectOption } from '@hb/services/store/abstract-store-inject.service';

import { inject, Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { Money } from '@ap/interface-bspc-core';
import { CreditCard } from '@ap/ap-interface-apix-hypotheken-berekening';

@Injectable({
    providedIn: 'root'
})
export class CreditCardStoreService extends AbstractStoreInjectOption<CreditCardQuestion> implements OnDestroy {
    protected readonly store = inject(FinancialObligationsStoreService);
    protected readonly lastQuestion = CreditCardQuestion.SUMMARY;

    private creditCardSubscription: Subscription;
    private readonly multipleInstallmentsSubject: FieldSubject<boolean>;
    private readonly amountSubject: FieldSubject<Money>;

    constructor() {
        super();
        this.init();

        const initialValues = this.getInitialValues();
        this.multipleInstallmentsSubject = new FieldSubject<boolean>(initialValues?.multiple_installments);
        this.amountSubject = new FieldSubject<Money>(initialValues?.amount);
        this.subscriptions();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.activeQuestionSubject.unsubscribe();
        this.multipleInstallmentsSubject.unsubscribe();
        this.amountSubject.unsubscribe();
        this.creditCardSubscription.unsubscribe();
    }

    update(): void {
        this.store.setCreditCard(
            new CreditCardBuilder()
                .withAmount(this.amountSubject.value)
                .withMultipleInstallments(this.multipleInstallmentsSubject.value)
                .build()
        );
    }

    isActiveQuestion(question: CreditCardQuestion): boolean {
        switch (question) {
            case CreditCardQuestion.SEVERAL_INSTALLMENTS:
                return true;
            case CreditCardQuestion.AMOUNT:
            case CreditCardQuestion.TIP:
                return this.getMultipleInstallments();
            case CreditCardQuestion.SUMMARY:
            default:
                return false;
        }
    }

    setAmount(amount: string): void {
        this.amountSubject.next(money(amount));
    }

    getAmount(): Money {
        return this.amountSubject.value;
    }

    getMultipleInstallments(): boolean {
        return this.multipleInstallmentsSubject.value;
    }

    setMultipleInstallments(multipleInstallments: boolean): void {
        this.multipleInstallmentsSubject.next(multipleInstallments);
    }

    setCreditCardTipFinished(): void {
        this.nextQuestion();
    }

    getInitialValues(): CreditCard {
        return this.store.getFinancialObligationState()?.credit_card;
    }

    private subscriptions(): void {
        this.subscribeCreditCardOption();
        this.subscribeFieldSubjects();
    }

    private subscribeFieldSubjects() {
        this.amountSubject.asObservable().subscribe(() => this.update());
        this.multipleInstallmentsSubject.asObservable().subscribe((multipleInstallments) => {
            if (multipleInstallments) {
                if (this.amountSubject.value) {
                    this.update();
                }
            } else {
                this.amountSubject.next(null);
                this.update();
            }
            this.nextQuestion();
        });
    }

    private subscribeCreditCardOption() {
        this.creditCardSubscription = this.store.getFinancialObligationsOptions().subscribe((options) => {
            if (!options.creditCard) {
                this.amountSubject.next(null);
                this.multipleInstallmentsSubject.next(null);
            }
        });
    }
}
